import httpClient, { ExtendedAxiosRequestConfig } from '../../api/httpClient';
import { queryClient } from '../../api/queryClient';

// Interfaz para los datos de parroquia
export interface Parish {
  id: number;
  name: string;
  code: string;
  cantonId?: number;
  cantonName?: string;
  provinceId?: number;
  provinceName?: string;
  // Añade aquí cualquier otro campo que tenga tu modelo de parroquia
}

// Clave para la caché de todas las parroquias
const ALL_PARISHES_CACHE_KEY = 'all-parishes-data';

/**
 * Obtiene la lista de parroquias con soporte de caché
 * @param parishName Nombre opcional para filtrar parroquias
 * @param useCache Si es true, intentará usar la caché primero
 * @returns Lista de parroquias filtrada
 */
async function getParishList(parishName: string = '', useCache: boolean = true): Promise<Parish[]> {
  try {
    // Normalizar el parámetro de búsqueda
    const normalizedParishName = parishName?.trim() || '';

    // Si useCache es true, intentamos usar la caché
    if (useCache) {
      // Intentar obtener datos de la caché primero
      const cachedData = await getCachedParishes();

      // Si tenemos datos en caché
      if (cachedData) {
        if (normalizedParishName) {
          return filterParishesLocally(cachedData, normalizedParishName);
        } else {
          return cachedData;
        }
      }
    }

    // Configuración de caché para la solicitud
    const config: ExtendedAxiosRequestConfig = {
      cache: {
        enabled: true,
        duration: 24 * 60 * 60 * 1000, // 1 día de staleTime
        cacheTime: 2 * 24 * 60 * 60 * 1000, // 2 días en caché total
        key: parishName ? `parish-list-${parishName}` : ALL_PARISHES_CACHE_KEY,
        refetchOnWindowFocus: false,
      },
    };

    // Siempre incluir parishName como parámetro, incluso cuando está vacío
    const url = `/api/address/parish-list?parishName=${parishName || ''}`;
    const response = await httpClient.get<Parish[]>(url, config);

    // Si la respuesta es exitosa y no hay filtro, almacenamos en caché global
    if (response && !parishName) {
      // Actualizamos la caché de todas las parroquias
      queryClient.setQueryData([ALL_PARISHES_CACHE_KEY], response);
    }

    return response || [];
  } catch (error) {
    return [];
  }
}

/**
 * Obtiene todas las parroquias de la caché si están disponibles
 */
async function getCachedParishes(): Promise<Parish[] | undefined> {
  // Verificar si tenemos datos en caché
  const cachedData = queryClient.getQueryData<Parish[]>([ALL_PARISHES_CACHE_KEY]);

  if (cachedData) {
    return cachedData;
  }

  return undefined;
}

/**
 * Filtra parroquias localmente basado en el nombre
 */
/**
 * Filtra parroquias localmente basado en coincidencias parciales en cualquier parte del texto
 * @param parishes Lista de parroquias a filtrar
 * @param filterText Texto para filtrar (puede estar en cualquier parte del nombre, código, cantón o provincia)
 * @returns Lista filtrada de parroquias
 */
function filterParishesLocally(parishes: Parish[], filterText: string): Parish[] {
  // Si no hay texto de filtro, devolver todas las parroquias
  if (!filterText || filterText.trim() === '') {
    return parishes;
  }

  // Normalizar el texto de filtro (minúsculas y sin espacios extra)
  const normalizedFilter = filterText.toLowerCase().trim();

  // Filtrar por coincidencia parcial en cualquier parte del texto
  return parishes.filter(
    (parish) =>
      (parish.name && parish.name.toLowerCase().includes(normalizedFilter)) ||
      (parish.code && parish.code.toLowerCase().includes(normalizedFilter)) ||
      (parish.cantonName && parish.cantonName.toLowerCase().includes(normalizedFilter)) ||
      (parish.provinceName && parish.provinceName.toLowerCase().includes(normalizedFilter)),
  );
}

/**
 * Invalida la caché de parroquias
 */
function invalidateParishCache(): void {
  queryClient.invalidateQueries({ queryKey: [ALL_PARISHES_CACHE_KEY] });
  // También podríamos invalidar todas las consultas que comiencen con 'parish-list-'
  queryClient.invalidateQueries({
    predicate: (query) => {
      const queryKey = Array.isArray(query.queryKey) ? query.queryKey[0] : query.queryKey;
      return typeof queryKey === 'string' && queryKey.startsWith('parish-list-');
    },
  });
}

/**
 * Precarga todas las parroquias en caché
 */
async function preloadAllParishes(): Promise<void> {
  await getParishList('', true);
}

export default {
  getParishList,
  invalidateParishCache,
  preloadAllParishes,
};
